
import store from "@/store";
import { defineComponent, ref, computed, watch, inject } from "@vue/runtime-core";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
  format,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import SelectInput from "../inputs/SelectInput.vue";
import InputText from "../inputs/InputText.vue";
import helpers from "@/helpers";
import { getThemeColor } from "@/branding";
import { FormValidatedValuesKey, FormValidatedUpdateKey } from "../../types/inject";
import { StatusDiaJanela } from "@/types/janela_agendamento";

export default defineComponent({
  props: {
    modelValue: {
      type: Date,
      required: false,
    },
  },
  components: {
    SelectInput,
    InputText,
  },
  setup(props, { emit }) {
    const theme = getThemeColor();

    const values = inject(FormValidatedValuesKey, {});
    const onUpdate = inject(FormValidatedUpdateKey);

    const setorSelecionado = computed(() => String(values.setor ?? ""));
    const operacaoSelecionada = computed(() => String(values.operacao ?? ""));

    // Preenche o Período automaticamente a partir da previsão de chegada
    // (mesma regra do backend: <12h manhã, <18h tarde, senão noite).
    const previsaoChegada = computed(() =>
      String(values.previsao_horario_chegada ?? "")
    );

    watch(previsaoChegada, (novo) => {
      if (!novo) {
        return;
      }

      const hora = parseInt(novo.substring(0, 2));

      if (isNaN(hora)) {
        return;
      }

      const periodo = hora < 12 ? 1 : hora < 18 ? 2 : 3;

      if (values.periodo != periodo && typeof onUpdate === "function") {
        onUpdate({ periodo });
      }
    });

    const usaJanelas = computed(
      () => !!store.state.setores_usa_janelas[setorSelecionado.value]
    );

    const disponibilidadeMes = computed(
      () => store.state.disponibilidade_mes as Record<string, StatusDiaJanela>
    );

    const retorna2 = (number: number) => {
      if (number < 10) return `0${number}`;
      return number;
    };

    const dataDoDia = (dia: number) => {
      const diaReferencia = retorna2(dia);
      const mesReferencia = retorna2((dataRef.value || new Date()).getMonth() + 1);
      const anoReferencia = (dataRef.value || new Date()).getFullYear();

      return `${anoReferencia}-${mesReferencia}-${diaReferencia}`;
    };

    const ehDomingo = (dia: number) => {
      const referencia = dataRef.value || new Date();
      return new Date(referencia.getFullYear(), referencia.getMonth(), dia).getDay() == 0;
    };

    const statusDoDia = (dia: number): StatusDiaJanela | undefined => {
      if (!usaJanelas.value) {
        return undefined;
      }
      return disponibilidadeMes.value[dataDoDia(dia)];
    };

    const naoPodeAgendar = (dia: number) => {
      if (dataDoDia(dia) < helpers.getDataAtual()) {
        return true;
      }

      // Domingo só é bloqueado fixo para setores sem janelas de horário;
      // com janelas, quem decide é a disponibilidade retornada pela API.
      if (ehDomingo(dia) && !usaJanelas.value) {
        return true;
      }

      const status = statusDoDia(dia);

      return status == "sem_expediente" || status == "bloqueado";
    };

    const diaSelecionado = (dia: number) =>
      dia == dataRef.value.getDate() &&
      ((dataRef.value.getFullYear() == parseInt(helpers.getDataAtual("yyyy")) &&
        dataRef.value.getMonth() >= (props.modelValue || new Date()).getMonth()) ||
        dataRef.value.getFullYear() > parseInt(helpers.getDataAtual("yyyy")));

    const classesDoDia = (dia: number) => {
      const base =
        "rounded-lg p-1 md:h-12 text-sm md:text-lg font-medium border " +
        "flex items-center justify-center transition-colors duration-100";

      if (diaSelecionado(dia)) {
        return `${base} bg-${theme}-800 border-${theme}-800 text-white font-bold shadow`;
      }

      if (naoPodeAgendar(dia)) {
        return `${base} bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed`;
      }

      if (statusDoDia(dia) == "lotado") {
        return `${base} bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200 cursor-pointer`;
      }

      return `${base} bg-white border-gray-400 text-gray-800 hover:border-${theme}-400 hover:bg-${theme}-50 cursor-pointer`;
    };

    const tituloDoDia = (dia: number) => {
      if (dataDoDia(dia) < helpers.getDataAtual()) {
        return "Data passada";
      }
      if (ehDomingo(dia) && !usaJanelas.value) {
        return "Não há expediente aos domingos";
      }
      const status = statusDoDia(dia);
      if (status == "sem_expediente") {
        return "Sem expediente";
      }
      if (status == "bloqueado") {
        return "Dia bloqueado";
      }
      if (status == "lotado") {
        return "Sem vagas";
      }
      return "";
    };

    const carregandoDisponibilidade = ref(false);

    const carregarDisponibilidadeMes = async () => {
      if (!usaJanelas.value || !setorSelecionado.value) {
        store.commit("SET_DISPONIBILIDADE_MES", {});
        return;
      }

      // Limpa o mês anterior para os dias não "piscarem" como disponíveis
      // enquanto a nova disponibilidade carrega.
      store.commit("SET_DISPONIBILIDADE_MES", {});
      carregandoDisponibilidade.value = true;

      try {
        const referencia = dataRef.value || new Date();
        await store.dispatch("getDisponibilidadeMes", {
          setor_ukey: setorSelecionado.value,
          ano: referencia.getFullYear(),
          mes: referencia.getMonth() + 1,
          operacao: operacaoSelecionada.value,
        });
      } finally {
        carregandoDisponibilidade.value = false;
      }
    };

    const formatDate = (date: Date | string, formatStr = "dd/MM/yyyy") => {
      if (!date) {
        return "";
      }
      try {
        if (typeof date === "string") {
          return format(parseISO(date), formatStr, {
            locale: ptBR,
          });
        }
        return format(date, formatStr, {
          locale: ptBR,
        });
      } catch (error) {
        console.log("error on format date", date);
        return "";
      }
    };

    let dataRef = ref(
      new Date(props.modelValue || Date.now()).getTime() > 0
        ? new Date(props.modelValue || Date.now())
        : new Date()
    );
    let diaInicialSemana = computed(
      () => parseInt(formatDate(startOfMonth(dataRef.value.getTime()), "i")) + 1
    );
    let diaFinalMes = computed(() =>
      endOfMonth(dataRef.value.getTime()).getDate()
    );
    let arrDias = computed(() => {
      let arrds = new Array(diaFinalMes.value).fill(1);
      for (const k in arrds) {
        arrds[k] = parseInt(k) + 1;
      }
      return arrds;
    });

    const handleClickDia = (dia: number) => {
      const novaData = new Date(
        dataRef.value.getFullYear(),
        dataRef.value.getMonth(),
        dia,
        dataRef.value.getHours(),
        dataRef.value.getMinutes(),
        dataRef.value.getSeconds()
      );
      dataRef.value = novaData;
      setPropDate(novaData);
    };

    const setPropDate = (date: Date) => {
      emit("update:modelValue", formatDate(date, "yyyy-MM-dd"));
    };

    watch(
      () => props.modelValue,
      (newValue) => {
        if (!!newValue && newValue?.getTime() != dataRef.value.getTime()) {
          dataRef.value = newValue;
        }
      }
    );

    const chaveDisponibilidade = computed(
      () =>
        `${usaJanelas.value}|${setorSelecionado.value}|${operacaoSelecionada.value}|` +
        `${dataRef.value.getFullYear()}-${dataRef.value.getMonth() + 1}`
    );

    watch(chaveDisponibilidade, carregarDisponibilidadeMes, { immediate: true });

    const getDataPorExtenso = (data: any) => {
      const dia = formatDate(data, "dd");
      const mes = formatDate(data, "MMMM");
      const ano = formatDate(data, "yyyy");

      return `${dia} de ${mes} de ${ano}`;
    };

    const quantidadeRegistros = computed(() => {
      const retorno: { name: string; value: number }[] = [];

      for (let index = 2; index <= 50; index++) {
        retorno.push({
          name: `Duplicar este agendamento ${index} vezes`,
          value: index,
        });
      }

      return [{ name: "Apenas este agendamento", value: 1 }, ...retorno];
    });

    const usuario_local_recebimento_graos = computed(
      () => store.state.acessos.usuario_local_recebimento_graos
    );

    return {
      theme,
      dataRef,
      formatDate,
      diaInicialSemana,
      arrDias,
      handleClickDia,
      subMonths,
      addMonths,
      setPropDate,
      getDataPorExtenso,
      quantidadeRegistros,
      usuario_local_recebimento_graos,
      naoPodeAgendar,
      usaJanelas,
      carregandoDisponibilidade,
      classesDoDia,
      tituloDoDia,
      helpers,
    };
  },
});
